import { assign, fromPromise, setup } from "xstate";

import { sqliteApi } from "~/state/api/sqlite";
import {
	type FeatureFlag,
	type Flag,
	allFlags,
	initialFlags,
} from "~/state/types/feature-flags.types";

export const featureFlagsMachine = setup({
	types: {
		context: {} as { flags: Record<Flag, boolean> },
		events: {} as { type: "featureFlag.update"; payload: FeatureFlag },
	},
	actors: {
		loadFeatureFlags: fromPromise(async () => {
			const dbFlags = await sqliteApi.featureFlags.listAll();

			if (dbFlags === undefined) {
				return initialFlags.reduce(
					(acc, flag) => ({
						...acc,
						[flag.id]: flag.enabled,
					}),
					{} as Record<Flag, boolean>
				);
			}

			const flagMap = dbFlags.reduce(
				(acc, flag) => ({
					...acc,
					[flag.id as Flag]: flag.enabled === "true",
				}),
				{} as Record<Flag, boolean>
			);

			// Ensure all flags have a value
			return allFlags.reduce(
				(acc, flagId) => ({
					...acc,
					[flagId]: flagMap[flagId] ?? false,
				}),
				{} as Record<Flag, boolean>
			);
		}),
		persistFeatureFlags: fromPromise(
			async ({ input: { flags } }: { input: { flags: Record<Flag, boolean> } }) => {
				await Promise.allSettled(
					Object.entries(flags).map(async ([id, enabled]) => {
						await sqliteApi.featureFlags.upsert({
							id: id as Flag,
							enabled,
						});
					})
				);

				return flags;
			}
		),
	},
	actions: {
		setFlags: assign(({ context, event }) => {
			if (event.type !== "featureFlag.update") {
				return {};
			}

			return {
				flags: {
					...context.flags,
					[event.payload.id]: event.payload.enabled,
				},
			};
		}),
	},
}).createMachine({
	id: "featureFlags",
	initial: "loading",
	context: {
		flags: initialFlags.reduce(
			(acc, flag) => ({
				...acc,
				[flag.id]: flag.enabled,
			}),
			{} as Record<Flag, boolean>
		),
	},
	states: {
		loading: {
			invoke: {
				src: "loadFeatureFlags",
				onDone: {
					actions: assign(({ event }) => ({
						flags: event.output,
					})),
					target: "idle",
				},
				onError: "error",
			},
		},
		idle: {
			on: {
				"featureFlag.update": {
					actions: "setFlags",
					target: "persisting",
				},
			},
		},
		persisting: {
			invoke: {
				src: "persistFeatureFlags",
				input: ({ context }) => {
					return { flags: context.flags };
				},
				onDone: "loading",
				onError: "error",
			},
		},
		error: {
			type: "final",
		},
	},
});
